<script setup lang="ts">
import { computed, onMounted, onUnmounted, PropType, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { GroupChartSummary, Participant, ProductLanguages, Project, ProjectRole } from "../../../types/PersologOnlineTypes.ts";
import { ProfileAdministrationAPI, ProfileContractorAPI } from "../../../api";
import { fetchRoles, getGroupChartForProduct, GROUP_CHART_FOR_361, groupChartToastGroup } from "../../../utils/ProjectUtils.ts";
import { openPdfInNewTab } from "../../../utils/PdfUtils.ts";
import GroupChartItem from "./GroupChartItem.vue";
import GroupChartCreateDialog from "./GroupChartCreateDialog.vue";

const { t } = useI18n();

const props = defineProps({
    project: {
        type: Object as PropType<Project>,
        required: true,
    },
    participants: {
        type: Array as PropType<Participant[]>,
        default: () => [],
    },
    languages: {
        type: Object as PropType<ProductLanguages | null>,
        default: null,
    },
    openChartId: {
        type: Number as PropType<number | null>,
        default: null,
    },
});

const emits = defineEmits(["open:benchmark", "tab:opened", "updateProject"]);

const POLL_INTERVAL_MS = 30000;
// Keep polling for a short window after a create/regenerate so a newly generated language appears.
const ACTION_POLL_WINDOW_MS = 180000;

const charts = ref<GroupChartSummary[]>([]);
const roles = ref<ProjectRole[]>([]);
const groupChartProduct = ref("");
const createDialogVisible = ref(false);
const collapsed = ref(true);

// Heading shows the chart count so it stays informative while the panel is collapsed.
const panelHeader = computed(() => {
    const heading = t("project-details.group-chart.heading");
    return charts.value.length ? `${heading} (${charts.value.length})` : heading;
});

let intervalID: ReturnType<typeof setInterval> | undefined;
const pollUntil = ref(0);

const selectableParticipants = computed(() =>
    props.participants.filter((p) => p.status === "GENERATED" || p.status === "NOTIFIED"),
);

/**
 * Whether the project has participants eligible for a group chart. For in119 (GROUP_CHART_FOR_361)
 * at least two generated participants are needed, exactly one of which is a FEEDBACK_RECIPIENT.
 */
const canCreateChart = computed(() => {
    if (groupChartProduct.value !== GROUP_CHART_FOR_361) {
        return selectableParticipants.value.length > 0;
    }
    const generated = selectableParticipants.value;
    if (generated.length < 2) return false;
    const roleById = new Map(roles.value.map((r) => [r.id, r.role]));
    return generated.filter((p) => roleById.get(p.roleId) === "FEEDBACK_RECIPIENT").length === 1;
});

// Shown under the disabled create button, so the trainer knows what is missing instead of guessing.
const showRequirements361 = computed(
    () => groupChartProduct.value === GROUP_CHART_FOR_361 && roles.value.length > 0 && !canCreateChart.value,
);
const feedbackRecipientName = computed(() => roles.value.find((r) => r.role === "FEEDBACK_RECIPIENT")?.name);

// Charts whose PDFs were deleted (RDT-475) and that the trainer asked to create again: generating until a PDF is back.
const regenerationRequested = ref(new Set<number>());

// A chart whose PDFs were deleted has no languages either, but nothing is generating it unless it was requested.
const isGenerating = (chart: GroupChartSummary) =>
    chart.languages.length === 0 && (!chart.pdfDeleted || regenerationRequested.value.has(chart.groupChartId));

const hasPending = computed(() => charts.value.some(isGenerating));

async function fetchCharts() {
    if (!props.project.id) return;
    const wasPending = hasPending.value;
    try {
        charts.value = await ProfileContractorAPI.getGroupChartsForProject(props.project.id);
        charts.value
            .filter((c) => c.languages.length > 0)
            .forEach((c) => regenerationRequested.value.delete(c.groupChartId));
    } catch (error) {
        console.error("Error fetching group charts:", error);
    }
    // When a generation just finished (pending -> ready), the backend has flipped the project
    // status; refresh the project so the rest of the details view stays in sync.
    if (wasPending && !hasPending.value) {
        await refreshProject();
    }
    ensurePolling();
}

async function refreshProject() {
    try {
        const updatedProject = await ProfileAdministrationAPI.getProject(
            props.project.trainer ?? "",
            props.project.id!,
        );
        if (updatedProject) emits("updateProject", updatedProject);
    } catch (error) {
        console.error("Error refreshing project:", error);
    }
}

/**
 * Polls while a chart is generating (no languages yet) or briefly after a create/regenerate, then
 * stops — avoiding a permanent interval.
 */
function ensurePolling() {
    const shouldPoll = hasPending.value || Date.now() < pollUntil.value;
    if (shouldPoll && intervalID === undefined) {
        intervalID = setInterval(fetchCharts, POLL_INTERVAL_MS);
    } else if (!shouldPoll && intervalID !== undefined) {
        clearInterval(intervalID);
        intervalID = undefined;
    }
}

function onCreated(chart: GroupChartSummary) {
    charts.value = [...charts.value, chart];
    pollUntil.value = Date.now() + ACTION_POLL_WINDOW_MS;
    ensurePolling();
}

function onDeleted(groupChartId: number) {
    charts.value = charts.value.filter((c) => c.groupChartId !== groupChartId);
}

function onChanged(groupChartId: number) {
    // Also for a chart that looks fine here: its PDFs may have gone since the list was loaded. The mark only
    // counts while the chart has no PDF and is dropped as soon as one is back.
    regenerationRequested.value.add(groupChartId);
    pollUntil.value = Date.now() + ACTION_POLL_WINDOW_MS;
    fetchCharts();
}

// Deleting a participant, or their revoking, takes the PDFs of their group charts with them (RDT-475):
// reload the charts whenever the project's participants change, so the list shows it right away.
const participantsKey = computed(() => props.participants.map((p) => `${p.id}:${p.status}`).join(","));
watch(participantsKey, () => fetchCharts());

onMounted(async () => {
    groupChartProduct.value = (await getGroupChartForProduct(props.project.product)) || props.project.product;
    // With their display names: the create dialog labels participants with them.
    roles.value = await fetchRoles(props.project);
    await fetchCharts();
});

onUnmounted(() => {
    if (intervalID !== undefined) clearInterval(intervalID);
});

// Guards against the watcher below (which also fires on `charts` updates) re-opening the same chart
// while its download/navigation is already in flight — `openChartId` is only cleared after the awaits.
let openingChartId: number | null = null;

/**
 * Deep link from a group-chart email (?projectId=…&chartId=…): open that specific chart's PDF in
 * the in-app viewer, in its first generated language. Returns early (without signalling the parent)
 * while the charts are still loading or the chart is still generating, so the watcher below retries
 * once the charts arrive; once handled it tells the parent to clear the id.
 */
async function openChartFromDeepLink(chartId: number) {
    if (openingChartId === chartId) return;
    const chart = charts.value.find((c) => c.groupChartId === chartId);
    if (chart && chart.languages.length === 0 && !isGenerating(chart)) {
        // Its PDFs were deleted: show the chart with the reason instead of waiting for a PDF that never comes.
        collapsed.value = false;
        emits("tab:opened");
        return;
    }
    if (!chart || chart.languages.length === 0) return;
    openingChartId = chartId;
    collapsed.value = false;
    const language = chart.languages[0];
    try {
        const product = chart.product ?? props.project.product;
        const blob = await ProfileContractorAPI.downloadPdf(product, chart.groupChartId, language);
        await openPdfInNewTab(blob, `${chart.label}-${language}`);
    } catch (error) {
        console.error("Error opening group chart PDF from link:", error);
    } finally {
        emits("tab:opened");
    }
}

watch(
    [() => props.openChartId, charts],
    () => {
        if (props.openChartId != null) openChartFromDeepLink(props.openChartId);
    },
    { immediate: true },
);
</script>

<template>
    <Toast :group="groupChartToastGroup(project.id)" />
    <Panel
        :id="`group-chart_panel-${project.id}`"
        toggleable
        v-model:collapsed="collapsed"
        :header="panelHeader"
        class="mt-4"
        :pt="{ header: { onClick: () => { collapsed = !collapsed; }, style: 'cursor: pointer' }, pcToggleButton: { root: { onClick: (e: MouseEvent) => e.stopPropagation() } } }"
    >
        <Button
            :id="`create-group-chart_button-${project.id}`"
            class="mb-4"
            icon="pi pi-plus"
            size="small"
            :disabled="!canCreateChart"
            :label="t('project-details.group-chart.create-group-chart')"
            @click="createDialogVisible = true"
        />
        <p
            v-if="showRequirements361"
            :id="`create-group-chart_requirements-${project.id}`"
            class="text-sm text-content-muted mt-0 mb-4"
        >
            {{ t("project-details.group-chart.requirements-361", { role: feedbackRecipientName }) }}
        </p>

        <p v-if="charts.length === 0" class="text-sm text-content-muted m-0">
            {{ t("project-details.group-chart.empty") }}
        </p>

        <div v-else class="flex flex-col">
            <GroupChartItem
                v-for="(chart, index) in charts"
                :key="chart.groupChartId"
                :class="{ 'border-t border-border-ui': index > 0 }"
                :chart="chart"
                :project="project"
                :participants="participants"
                :languages="languages"
                :regeneration-requested="regenerationRequested.has(chart.groupChartId)"
                :feedback-recipient-role="feedbackRecipientName"
                @deleted="onDeleted"
                @changed="onChanged"
                @open:benchmark="(id: number) => emits('open:benchmark', id)"
            />
        </div>

        <GroupChartCreateDialog
            v-if="createDialogVisible"
            :project="project"
            :participants="selectableParticipants"
            :group-chart-product="groupChartProduct"
            :languages="languages"
            :roles="roles"
            @created="onCreated"
            @close="createDialogVisible = false"
        />
    </Panel>
</template>
